import React from 'react';
import { Box, Typography, Paper, Chip, Avatar, Stack, alpha, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GroupsIcon from '@mui/icons-material/Groups';
import LabelIcon from '@mui/icons-material/Label';
import { useTranslation } from 'react-i18next';
import { stringToColor } from '../helpers';

const FirmHero = ({ firm }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const firmLabel = firm.firm_short_name || firm.firm_name || '?';
  const firmInitials = firmLabel.substring(0, 2).toUpperCase();
  const firmColor = stringToColor(firm.firm_name || '?');
  const sectors = firm.firm_sectors?.map(fs => fs.sector?.sector_name).filter(Boolean) || [];
  const partners = firm.firm_partners?.map(fp => fp.partner?.partner_name).filter(Boolean) || [];

  return (
    <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
      <Box sx={{ height: 6, background: `linear-gradient(90deg, ${firmColor}, ${alpha(firmColor, 0.3)})` }} />
      <Box
        sx={{
          p: { xs: 2.5, md: 3.5 },
          background: isDark
            ? `radial-gradient(ellipse at top left, ${alpha(firmColor, 0.12)} 0%, transparent 60%)`
            : `radial-gradient(ellipse at top left, ${alpha(firmColor, 0.07)} 0%, transparent 60%)`,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={3} alignItems={{ sm: 'center' }}>
          <Avatar
            sx={{
              width: 72,
              height: 72,
              bgcolor: firmColor,
              fontSize: 26,
              fontWeight: 900,
              flexShrink: 0,
              borderRadius: 2.5,
              boxShadow: `0 8px 24px ${alpha(firmColor, 0.45)}`,
              letterSpacing: -1,
            }}
          >
            {firmInitials}
          </Avatar>

          <Box flex={1} minWidth={0}>
            <Stack direction="row" alignItems="baseline" gap={1.5} flexWrap="wrap" mb={1}>
              <Typography variant="h4" fontWeight={900} lineHeight={1.1}>
                {firm.firm_name || '—'}
              </Typography>
              {firm.firm_short_name && firm.firm_name !== firm.firm_short_name && (
                <Typography variant="h6" color="text.secondary" fontWeight={400}>
                  {firm.firm_short_name}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
              <Stack direction="row" alignItems="center" gap={0.5}>
                {firm.is_established
                  ? <CheckCircleIcon sx={{ fontSize: 15, color: 'success.main' }} />
                  : <CancelIcon sx={{ fontSize: 15, color: 'text.disabled' }} />}
                <Typography variant="caption" fontWeight={600} color={firm.is_established ? 'success.main' : 'text.disabled'}>
                  {firm.is_established ? t('firmDetail.established') : t('firmDetail.notEstablished')}
                </Typography>
              </Stack>

              {firm.firm_city && (
                <>
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'divider' }} />
                  <Stack direction="row" alignItems="center" gap={0.4}>
                    <LocationOnIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      {firm.firm_city}
                    </Typography>
                  </Stack>
                </>
              )}

              {firm.partner_referral && (
                <>
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'divider' }} />
                  <Stack direction="row" alignItems="center" gap={0.4}>
                    <GroupsIcon sx={{ fontSize: 13, color: 'info.main' }} />
                    <Typography variant="caption" color="info.main" fontWeight={600}>
                      {t('firmDetail.partnerReferral')}
                    </Typography>
                  </Stack>
                </>
              )}

              {sectors.length > 0 && <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'divider' }} />}
              {sectors.map(s => (
                <Chip
                 onClick={() => {
                    }}
                  key={s}
                  icon={<LabelIcon sx={{ fontSize: '12px !important' }} />}
                  label={s}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 11,
                    bgcolor: alpha(firmColor, 0.1),
                    border: `1px solid ${alpha(firmColor, 0.2)}`,
                    color: isDark ? alpha(firmColor, 1.5) : firmColor,
                    '& .MuiChip-icon': { color: 'inherit' },
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              ))}

              {partners.length > 0 && <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'divider' }} />}
              {partners.map(p => (
                <Chip
                 onClick={() => {
                    }}
                  key={p}
                  label={p}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
};

export default FirmHero;
